import React, { useEffect, useState } from 'react';
import {
  BirdzDialog,
  BirdzNotif,
  useDialog,
  useNotif
} from '@applications-terrains/birdz-react-library';
import { IconButton } from '@mui/material';
import BTable from '../../../Tools/Table/BTable';
import { useTranslation } from 'react-i18next';
import { AddCircle, Edit } from '@mui/icons-material';
import DeleteIcon from '@mui/icons-material/Delete';
import ModalWithLoader, {
  ModalWithLoaderAction,
  RequestStatus
} from '../../../Tools/ModalWithLoader/ModalWithLoader';
import FP2EAdditionalConfigForm from './FP2EAdditionalConfigForm';
import { useGetCompanies } from '../../../../hooks/datarefs';
import axios from 'axios';
import useRefresh from '../../../../hooks/useRefresh';
import { FP2EItem } from './types';

export default function FP2EAdditionalConfig() {
  const { t } = useTranslation();
  const endpoint = '/api/bconnect/webapp/administrator/references/fp2e-additional-configurations/';
  const [action, setAction] = useState<ModalWithLoaderAction>(null);
  const [requestStatus, setRequestStatus] = useState<RequestStatus>(null);
  const [id, setId] = useState<number | null>(null);
  const { data: clients } = useGetCompanies();
  const { confirmDialog, closeDialog, dialogOptions } = useDialog();
  const { refresh, triggerRefresh } = useRefresh();
  const { notif, notifOptions } = useNotif();

  const listFields = [
    {
      name: 'BrandId',
      label: t('referencesTables.brand'),
      transform: (_: any, row: any) => {
        return `Code: ${row.brand_code ?? '-'} / Nom: ${row.brand_label ?? '-'}`;
      }
    },
    {
      name: 'ModelId',
      label: t('referencesTables.model'),
      transform: (_: any, row: any) => {
        return `Code: ${row.model_code ?? '-'} / Nom: ${row.model_label ?? '-'}`;
      }
    },
    {
      name: 'DiameterId',
      label: t('referencesTables.diameter'),
      transform: (_: any, row: any) => {
        return `Code: ${row.diameter_code ?? '-'} / Valeur: ${row.diameter_value ?? '-'} / Clé métier: ${row.diameter_meter_key ?? '-'}`;
      }
    },
    {
      name: 'MeterKey',
      label: t('referencesTables.meterKey')
    },
    {
      name: 'companies',
      label: t('referencesTables.clients'),
      transform: (value: number[]) => {
        return value.length ? `${value.length} / ${clients?.length}` : '-';
      }
    }
  ];

  const actions = [
    {
      name: 'edit',
      label: t('referencesTables.additionalConfiguration.actions.edit'),
      render: (item: FP2EItem) => {
        return (
          <IconButton
            onClick={() => {
              setAction('edit');
              setId(item.Id);
            }}
          >
            <Edit fontSize="small" color="action" />
          </IconButton>
        );
      }
    },
    {
      name: 'delete',
      label: t('referencesTables.additionalConfiguration.actions.delete'),
      render: (param: FP2EItem) => {
        return (
          <IconButton
            onClick={() => {
              confirmDialog({
                title: t('referencesTables.additionalConfiguration.actions.delete'),
                content: t('referencesTables.additionalConfiguration.actions.deleteTypeContent'),
                onValidate: () => {
                  setAction('delete');
                  axios
                    .delete(
                      `/api/bconnect/webapp/administrator/references/fp2e-additional-configurations/${param.Id}/`
                    )
                    .then(
                      () => {
                        setAction(null);
                        triggerRefresh();
                        notif({
                          content: t('referencesTables.deleteSuccess'),
                          type: 'success'
                        });
                      },
                      () => {
                        setAction(null);
                        notif({
                          content: t('referencesTables.deleteError'),
                          type: 'success'
                        });
                      }
                    );
                  closeDialog();
                },
                onCancel: () => {
                  closeDialog();
                }
              });
            }}
          >
            <DeleteIcon fontSize="small" />
          </IconButton>
        );
      }
    }
  ];

  const handleCloseModalWithLoader = () => {
    setAction(null);
    setRequestStatus(null);
    setId(null);
  };

  useEffect(() => {
    if (requestStatus === 'success') {
      triggerRefresh();
      setId(null);
    }
  }, [requestStatus, triggerRefresh]);

  return (
    <>
      <BTable
        model={'fp2e-additional-configurations'}
        endpoint={endpoint}
        fields={listFields}
        actions={actions}
        refresh={refresh}
        headerActions={[
          {
            name: t('referencesTables.additionalConfiguration.actions.add'),
            element: (
              <IconButton onClick={() => setAction('add')}>
                <AddCircle fontSize="small" color="action" />
              </IconButton>
            )
          }
        ]}
      />

      <BirdzDialog options={dialogOptions} />
      <BirdzNotif options={notifOptions} />

      <ModalWithLoader
        openModal={action === 'add' || action === 'edit'}
        onClose={() => handleCloseModalWithLoader()}
        onSuccess={() => handleCloseModalWithLoader()}
        action={
          action === 'delete'
            ? t('referencesTables.additionalConfiguration.modalMessages.deleteTypeAction')
            : action === 'edit'
              ? t('referencesTables.additionalConfiguration.modalMessages.editTypeAction')
              : t('referencesTables.additionalConfiguration.modalMessages.createTypection')
        }
        status={requestStatus}
        setStatus={setRequestStatus}
        showCloseButton={true}
      >
        <FP2EAdditionalConfigForm
          endpoint={endpoint}
          setRequestStatus={setRequestStatus}
          action={action}
          setAction={setAction}
          id={id}
        />
      </ModalWithLoader>
    </>
  );
}
